import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiMapPin, FiPhone, FiMail, FiClock, FiSend,
  FiUser, FiMessageSquare, FiCheckCircle
} from 'react-icons/fi';
import { FaFacebookF, FaLinkedinIn, FaInstagram, FaWhatsapp } from 'react-icons/fa';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    service: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [botField, setBotField] = useState('');
  const formLoadTime = useRef(Date.now());
  const lastSubmitTime = useRef(0);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check — bots fill hidden fields
    if (botField) return;

    // Time-based check — reject if submitted too fast (<3s)
    if (formLoadTime.current && Date.now() - formLoadTime.current < 3000) {
      setError('Please take a moment before submitting.');
      return;
    }

    // Rate limit — block submissions within 10s of each other
    if (Date.now() - lastSubmitTime.current < 10000) {
      setError('Please wait before submitting again.');
      return;
    }
    lastSubmitTime.current = Date.now();

    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORM_ACCESS_KEY,
          botcheck: '',
          ...formData,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFormData({ name: '', email: '', phone: '', subject: '', message: '', service: '' });
        }, 3000);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Failed to send. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <FiMapPin className="text-2xl" />,
      title: 'Visit Us',
      details: ['Metro Complex, Kuldharan Road', 'Karjat, Dist. Anagar', 'Maharashtra 414402'],
    },
    {
      icon: <FiPhone className="text-2xl" />,
      title: 'Call Us',
      details: ['+91 95276 22727', 'Toll Free: 1800 233 2727'],
    },
    {
      icon: <FiMail className="text-2xl" />,
      title: 'Email Us',
      details: ['nathkrupaenergy@gmail.com', 'nathkrupaenterprises27@gmail.com'],
    },
    {
      icon: <FiClock className="text-2xl" />,
      title: 'Working Hours',
      details: ['Monday - Saturday', '9:00 AM - 6:00 PM', 'Sunday: Closed'],
    },
  ];

  const offices = [
    {
      city: 'Nathkrupa Enterprises',
      address: 'Khandavi, tal. Karjat, Dist. A.nagar, Maharashtra 414401',
      phone: '+91 95276 22727',
    },
    {
      city: 'Nathkrupa Energy Pvt. Ltd.',
      address: 'Metro Complex, Kuldharan Road, Karjat, Dist. Anagar, Maharashtra 414402',
      phone: '+91 95276 22727',
    },
    {
      city: 'Factory – Nathkrupa Energy Pvt. Ltd.',
      address: 'Gat no. 570 Tukai Vasti, Kaudane Road, Khandavi, tal. Karjat, Dist. Anagar, Maharashtra 414401',
      phone: '+91 95276 22727',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden mt-20 md:mt-24">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-dark-900/90 via-dark-800/80 to-dark-900/70" />
        <div className="container relative z-10 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4 uppercase tracking-wide">Contact Us</h1>
            <p className="text-base md:text-xl text-white/90 mb-4">Get in touch with our solar experts for your energy needs</p>
            <div className="flex items-center justify-center gap-2 text-sm text-white/80">
              <Link to="/" className="hover:text-solar-400 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-solar-400">Contact</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-10 md:py-16 bg-gray-50 -mt-8 md:-mt-16 relative z-20">
        <div className="container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 shadow-lg text-center hover:shadow-xl transition-all duration-300 group rounded-2xl"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-solar-500 text-white flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform rounded-xl">
                  {info.icon}
                </div>
                <h3 className="text-lg font-bold text-dark-800 mb-3">{info.title}</h3>
                {info.details.map((detail, i) => (
                  <p key={i} className="text-dark-500 text-sm">{detail}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Get In Touch</span>
              <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Send Us a Message</h2>
              <p className="text-dark-500 mb-8">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>
              
              {submitted ? (
                <div className="bg-secondary-50 border border-secondary-200 p-10 text-center rounded-lg">
                  <FiCheckCircle className="text-5xl text-secondary-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-dark-800 mb-2">Thank You!</h3>
                  <p className="text-dark-500">Your message has been sent successfully. We'll contact you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot — hidden from humans, traps bots */}
                  <input
                    type="text"
                    name="botcheck"
                    value={botField}
                    onChange={(e) => setBotField(e.target.value)}
                    style={{ display: 'none' }}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="flex items-center gap-2 text-sm font-semibold text-dark-700 mb-2">
                        <FiUser className="text-primary-500" /> Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your name"
                        required
                        className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all rounded-lg"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="flex items-center gap-2 text-sm font-semibold text-dark-700 mb-2">
                        <FiMail className="text-primary-500" /> Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="flex items-center gap-2 text-sm font-semibold text-dark-700 mb-2">
                        <FiPhone className="text-primary-500" /> Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter your phone number"
                        required
                        className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all rounded-lg"
                      />
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-sm font-semibold text-dark-700 mb-2">
                        Service Interested In
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all rounded-lg"
                      >
                        <option value="">Select a service</option>
                        <option value="solar-pump">Solar Water Pump</option>
                        <option value="rooftop">Solar Rooftop</option>
                        <option value="panel">Solar Panels</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-dark-700 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all rounded-lg"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="flex items-center gap-2 text-sm font-semibold text-dark-700 mb-2">
                      <FiMessageSquare className="text-primary-500" /> Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your requirements..."
                      rows="5"
                      required
                      className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all resize-none rounded-lg"
                    ></textarea>
                  </div>
                  {error && <p className="text-red-500 text-sm">{error}</p>}
                  <button type="submit" disabled={loading} className="btn btn-primary disabled:opacity-70">
                    {loading ? 'Sending…' : <><FiSend className="inline mr-2" />Send Message</>}
                  </button>
                </form>
              )}
            </motion.div>

            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Quick Contact */}
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-8 text-white rounded-2xl">
                <h3 className="text-xl font-bold mb-3">Quick Contact</h3>
                <p className="text-white/85 mb-6">Need immediate assistance? Contact us directly:</p>
                <a href="tel:18002332727" className="flex items-center gap-3 text-2xl font-bold mb-6 hover:text-solar-300 transition-colors">
                  <FiPhone /> 1800 233 2727
                </a>
                {/* <a href="https://wa.me/919876543210" className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 font-semibold transition-all rounded-lg" target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp className="text-xl" /> Chat on WhatsApp
                </a> */}
              </div>

              {/* Social Links */}
              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-xl font-bold text-dark-800 mb-4">Follow Us</h3>
                <div className="flex gap-3">
                  <a href="https://www.facebook.com/profile.php?id=100067472019871" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-dark-800 text-white flex items-center justify-center hover:bg-primary-500 transition-all rounded-lg">
                    <FaFacebookF />
                  </a>
                  <a href="https://www.linkedin.com/company/nathkrupa-energy-pvt-ltd/" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-dark-800 text-white flex items-center justify-center hover:bg-primary-500 transition-all rounded-lg">
                    <FaLinkedinIn />
                  </a>
                  <a href="https://www.instagram.com/nathkrupa_energy_pvt.ltd/" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-dark-800 text-white flex items-center justify-center hover:bg-primary-500 transition-all rounded-lg">
                    <FaInstagram />
                  </a>
                </div>
              </div>

              {/* FAQ Link */}
              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-xl font-bold text-dark-800 mb-3">Have Questions?</h3>
                <p className="text-dark-500 text-sm mb-4">Check our frequently asked questions or contact our support team.</p>
                <Link to="/#faq" className="inline-flex items-center gap-2 border-2 border-dark-300 text-dark-600 px-5 py-2 font-semibold hover:border-primary-500 hover:text-primary-500 transition-all rounded-lg">
                  View FAQs
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <motion.div 
            className="text-center mb-8 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Our Offices</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Regional Offices</h2>
            <p className="text-dark-500 max-w-2xl mx-auto">
              Visit us at any of our regional offices across India.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {offices.map((office, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-primary-500 rounded-2xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <h4 className="text-lg font-bold text-dark-800 mb-3">{office.city}</h4>
                <p className="flex items-start gap-2 text-sm text-dark-500 mb-2">
                  <FiMapPin className="text-primary-500 mt-1 flex-shrink-0" /> {office.address}
                </p>
                <p className="flex items-center gap-2 text-sm text-dark-500">
                  <FiPhone className="text-primary-500" /> {office.phone}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section>
        <iframe
          title="Nath Krupa Energy Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d242118.14199917038!2d73.72287827812503!3d18.524564899999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bf2e67461101%3A0x828d43bf9d9ee343!2sPune%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1642159887254!5m2!1sen!2sin"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          className="grayscale hover:grayscale-0 transition-all duration-500"
        ></iframe>
      </section>
    </div>
  );
};

export default Contact;
